import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { get, has, isArray } from 'lodash';
import clsx from 'clsx';
import { Typography, useMediaQuery } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import Markdown from '@configuratorware/configurator-frontendgui/Framework/Components/Markdown';
import { t } from '@configuratorware/configurator-frontendgui/Framework/i18n';
import OptionAmount from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/OptionAmount';
import OptionGroup from '@configuratorware/configurator-frontendgui/App/Modules/Creator/Components/OptionGroup';
import Badge from '@material-ui/core/Badge';
import Icon from '@material-ui/core/Icon/Icon';

const styles = (theme) => ({
  root: {

    height: 35,
    width: 35,
    borderRadius: 100,
    margin: 'auto',
    border: '7px solid transparent',
    cursor: 'pointer',
    '&.hover': {
      border: '7px solid #F2F2F2',
    },
    '&:active, &:focus': {
      border: '7px solid #C9C9C9',
    },

  },
  contained: {
    borderRadius: 100,
    height: 32,
    border: '2px solid #707070',
    width: 32,
    '&.selected': {
    },

  },
  checkedBadge: {
    '& .MuiBadge-badge': {
      background: theme.palette.secondary.main,
      padding: 0,
      '& .MuiIcon-root': {
        color: theme.palette.secondary.text,
        fontSize: 10,
      },
    },
  },
});

const Option = ({
  className,
  onClick,
  onSelect,
  option,
  optionclassification,
  selected,
  classes,
}) => {
  const smallerScreen = useMediaQuery('(min-device-width : 300px) and (max-device-width : 340px)');
  const optionIdentifier = get(option, 'identifier', '');
  const amountIsSelectable = get(option, 'amount_is_selectable', false);
  const resultStatus = get(option, 'check_results.status', true);
  const optionAmount = get(option, 'amount', 0);

  const [hover, setHover] = useState(false);

  const isEnabled = () => get(option, 'stock.traffic_light', false) !== 'red';

  const onOptionSelected = () => {
    const optionClassificationIsMultiselect = get(optionclassification, 'is_multiselect', false);

    if (amountIsSelectable) {
      onSelect({
        ...option,
        amount: !optionAmount ? 1 : optionAmount,
      });
    } else if (optionClassificationIsMultiselect) {
      onSelect({
        ...option,
        amount: selected ? 0 : 1,
      });
    } else if (!selected) {
      onSelect({
        ...option,
        amount: 1,
      });
    }
  };

  const onOptionClicked = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (isEnabled()) {
      onOptionSelected();
      onClick();
    }
  };

  const getBackground = (_option) => {
    const idx = _option.attributes.findIndex((attribute) => attribute.identifier === 'colorValue');
    if (idx < 0) return {};
    if (_option.attributes[idx].values.length < 1) return {};
    return { backgroundColor: _option.attributes[idx].values[0].value };
  };

  const renderItem = () => {
    const enabled = isEnabled();

    const amountClass = amountIsSelectable && !smallerScreen ? classes.withAmount : '';
    const contentClassName = clsx(
      'content',
      className,
      optionIdentifier,
      amountClass,
      !enabled && 'disabled',
      resultStatus !== true && 'incompatible',
    );

    const toggleHover = (value) => {
      setHover(value);
    };
    return (

      <div
        className={contentClassName}
      >
        <div
          className={clsx(classes.root, selected && 'selected', hover && 'hover')}
          data-option={option.identifier}
          onMouseEnter={() => toggleHover(true)}
          onMouseLeave={() => toggleHover(false)}
        >
          {selected ? (
            <Badge
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              overlap="circle"
              className={classes.checkedBadge}
              badgeContent={<Icon>check</Icon>}
            >

              <div
                data-option={option.identifier}
                className={clsx(classes.contained, selected && 'selected')}
                onClick={onOptionClicked}
                style={getBackground(option)}
              />
            </Badge>
          ) : (

            <div
              className={clsx(classes.contained, selected && 'selected')}
              onClick={onOptionClicked}
              style={getBackground(option)}
            />
          )}
        </div>

      </div>
    );
  };

  return renderItem();
};

Option.defaultProps = {
  className: '',
  onClick: () => {},
  onSelect: () => {},
  onToggle: () => {},
  optionclassification: {},
  option: {},
  selected: false,
  translations: {},
  viewDetails: () => {},
  viewStockInformation: () => {},
  OptionAmountComponent: OptionAmount,
  OptionGroupComponent: OptionGroup,
};

Option.propTypes = {
  className: PropTypes.string,
  onClick: PropTypes.func,
  onSelect: PropTypes.func,
  onToggle: PropTypes.func,
  option: PropTypes.object,
  optionclassification: PropTypes.object,
  selected: PropTypes.bool,
  translations: PropTypes.object,
  viewDetails: PropTypes.func,
  viewStockInformation: PropTypes.func,
  OptionAmountComponent: PropTypes.elementType,
  OptionGroupComponent: PropTypes.elementType,
};

export default withStyles(styles, { name: 'Option' })(Option);
